import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { EligibilityPicker } from './EligibilityPicker.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Gst {
  vendorGstin?: string;
  gstinStatusCode?: string;
  invoiceGstinMatch?: boolean;
  hsnSacCode?: string;
  taxableAmount?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  cessAmount?: number;
  totalGstAmount?: number;
  validationStatusCode?: string;
  isReverseCharge?: boolean;
}

/** L15.11 — GST Validation: view GST validation for an eligibility. */
export function GstValidationPage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const [eligId, setEligId] = useState(params.get('eligibilityId') ?? '');
  const [data, setData] = useState<Gst | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    // Every validation run is kept; the newest comes first and is the one that counts.
    apiRequest<Gst[]>(`/v1/payments/gst/eligibility/${id.trim()}`, { token })
      .then((rows) => setData(rows[0] ?? null))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = params.get('eligibilityId');
    if (id) load(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const yn = (v?: boolean) => (v === undefined ? '—' : v ? t('finance.gst.yes') : t('finance.gst.no'));
  const row = (label: string, value: ReactNode) => (
    <tr style={{ borderBottom: '1px solid var(--color-border-default)' }}>
      <td style={{ padding: 8, color: 'var(--color-text-muted)' }}>{label}</td>
      <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{value}</td>
    </tr>
  );

  return (
    <div style={{ padding: 24, maxWidth: 640 }}>
      <h1>{t('finance.gst.title')}</h1>
      <EligibilityPicker
        value={eligId}
        onPick={(id) => { setEligId(id); setParams(id ? { eligibilityId: id } : {}); if (id) load(id); else setData(null); }}
      />
      {error && <p style={{ color: 'red' }} role="alert">{error}</p>}
      {loading && <p>{t('finance.gst.loading')}</p>}
      {!loading && !data && !error && <p>{t('finance.gst.empty')}</p>}
      {data && (
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--color-border-default)', borderRadius: 6 }}>
          <tbody>
            {row(t('finance.gst.rowGstin'), data.vendorGstin ?? '—')}
            {row(t('finance.gst.rowGstinStatus'), data.gstinStatusCode ?? '—')}
            {row(t('finance.gst.rowMatch'), yn(data.invoiceGstinMatch))}
            {row(t('finance.gst.rowHsn'), data.hsnSacCode ?? '—')}
            {row(t('finance.gst.rowTaxable'), money(data.taxableAmount ?? 0))}
            {row(t('finance.gst.rowCgst'), money(data.cgstAmount ?? 0))}
            {row(t('finance.gst.rowSgst'), money(data.sgstAmount ?? 0))}
            {row(t('finance.gst.rowIgst'), money(data.igstAmount ?? 0))}
            {row(t('finance.gst.rowCess'), money(data.cessAmount ?? 0))}
            {row(t('finance.gst.rowTotal'), money(data.totalGstAmount ?? 0))}
            {row(t('finance.gst.rowReverse'), data.isReverseCharge
              ? <span data-testid="gst-reverse-charge">{t('finance.gst.reverseChargePayable')}</span>
              : yn(data.isReverseCharge))}
            {row(t('finance.gst.rowStatus'), data.validationStatusCode ?? '—')}
          </tbody>
        </table>
      )}
    </div>
  );
}
